/**
 * /admin/v1/prompts/* API 客户端 · M05 Prompt 管理（S3 Day12）
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";

export type PromptStatus = "draft" | "published" | "archived";

export interface PromptVersionListItem {
	version_id: string;
	title: string;
	status: PromptStatus;
	region_count: number;
	main_agent_token_estimate: number;
	d_prime_token_estimate: number;
	updated_by: string | null;
	updated_at: string;
}

export interface PromptVersionListResponse {
	items: PromptVersionListItem[];
	total: number;
}

export interface DPrimeTemplateResponse {
	version_id: string;
	template: string;
	variables: string[];
	token_estimate: number;
	updated_by: string | null;
	updated_at: string;
}

export interface MainAgentTemplateResponse {
	version_id: string;
	template: string;
	variables: string[];
	token_estimate: number;
	updated_by: string | null;
	updated_at: string;
}

export interface MainAgentTemplatePatchRequest {
	version_id?: string;
	template: string;
	updated_by?: string | null;
	notes?: string | null;
}

export interface DPrimeTemplatePatchRequest {
	version_id?: string;
	template: string;
	updated_by?: string | null;
	notes?: string | null;
}

export interface PromptABConfigResponse {
	merchant_id: string;
	system_prompt_version_a: string;
	system_prompt_version_b: string | null;
	system_prompt_ab_ratio: number;
	updated_at: string;
}

export interface PromptABConfigPutRequest {
	system_prompt_version_a: string;
	system_prompt_version_b: string | null;
	system_prompt_ab_ratio: number;
}

export interface SandboxSkillFixture {
	skill_id: string;
	status: string;
	summary: string;
}

export interface SandboxRagSnippet {
	namespace: string;
	doc_id: string;
	content: string;
	score: number;
}

export interface PromptSandboxRequest {
	merchant_id: string;
	user_message: string;
	prompt_version?: string;
	allowed_skills: string[];
	d_prime_fixture: {
		auto_invoked_results: SandboxSkillFixture[];
		rag_snippets: SandboxRagSnippet[];
	};
}

export interface PromptSandboxPhasePreview {
	phase: string;
	status: string;
	summary: Record<string, unknown>;
}

export interface PromptSandboxResponse {
	prompt_version: string;
	sandbox_only: boolean;
	main_agent_prompt_preview: string;
	d_prime_preview: string;
	token_estimate: number;
	phase_preview: PromptSandboxPhasePreview[];
}

export function usePromptVersions() {
	return useQuery<PromptVersionListResponse>({
		queryKey: ["admin", "prompts", "versions"],
		queryFn: async () => {
			const { data } = await apiClient.get<PromptVersionListResponse>("/admin/v1/prompts/versions");
			return data;
		},
	});
}

export function useDPrimeTemplate(versionId = "m05-v1") {
	return useQuery<DPrimeTemplateResponse>({
		queryKey: ["admin", "prompts", "d-prime", versionId],
		queryFn: async () => {
			const { data } = await apiClient.get<DPrimeTemplateResponse>(
				"/admin/v1/prompts/prompt-templates/d-prime",
				{ params: { version_id: versionId } },
			);
			return data;
		},
	});
}

export function useMainAgentTemplate(versionId = "m05-v1") {
	return useQuery<MainAgentTemplateResponse>({
		queryKey: ["admin", "prompts", "main-agent", versionId],
		queryFn: async () => {
			const { data } = await apiClient.get<MainAgentTemplateResponse>(
				"/admin/v1/prompts/prompt-templates/main-agent",
				{ params: { version_id: versionId } },
			);
			return data;
		},
	});
}

export function usePatchMainAgentTemplate() {
	const queryClient = useQueryClient();
	return useMutation<MainAgentTemplateResponse, unknown, MainAgentTemplatePatchRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.patch<MainAgentTemplateResponse>(
				"/admin/v1/prompts/prompt-templates/main-agent",
				body,
			);
			return data;
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ["admin", "prompts", "versions"] });
			queryClient.invalidateQueries({
				queryKey: ["admin", "prompts", "main-agent", data.version_id],
			});
		},
	});
}

export function usePatchDPrimeTemplate() {
	const queryClient = useQueryClient();
	return useMutation<DPrimeTemplateResponse, unknown, DPrimeTemplatePatchRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.patch<DPrimeTemplateResponse>(
				"/admin/v1/prompts/prompt-templates/d-prime",
				body,
			);
			return data;
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ["admin", "prompts", "versions"] });
			queryClient.invalidateQueries({
				queryKey: ["admin", "prompts", "d-prime", data.version_id],
			});
		},
	});
}

export function usePromptABConfig(merchantId: string) {
	return useQuery<PromptABConfigResponse>({
		queryKey: ["admin", "prompts", "ab", merchantId],
		queryFn: async () => {
			const { data } = await apiClient.get<PromptABConfigResponse>(
				`/admin/v1/prompts/merchants/${encodeURIComponent(merchantId)}/system_prompt`,
			);
			return data;
		},
		enabled: merchantId.length > 0,
	});
}

export function usePutPromptABConfig(merchantId: string) {
	const queryClient = useQueryClient();
	return useMutation<PromptABConfigResponse, unknown, PromptABConfigPutRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.put<PromptABConfigResponse>(
				`/admin/v1/prompts/merchants/${encodeURIComponent(merchantId)}/system_prompt`,
				body,
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "prompts", "ab", merchantId] });
		},
	});
}

export function usePromptSandbox() {
	return useMutation<PromptSandboxResponse, unknown, PromptSandboxRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<PromptSandboxResponse>(
				"/admin/v1/prompts/sandbox",
				body,
			);
			return data;
		},
	});
}
